import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CSS/Services.css';
import { servicesData } from '../Helpers/ServicesHelper';

const Services = () => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false); // Start with false, will be updated after mount

  // Check scroll position and update button visibility
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      
      // Check if we're at the very beginning (first item fully visible)
      const atStart = scrollLeft === 0; // Exact check for start position
      
      // Check if we're at the very end (last item fully visible)
      const atEnd = Math.abs(scrollLeft - (scrollWidth - clientWidth)) <= 1; // Account for sub-pixel precision
      
      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
    }
  };

  // Scroll left function
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth = 405.33 + 30; // card width + gap
      scrollContainerRef.current.scrollBy({
        left: -cardWidth,
        behavior: 'smooth'
      });
    }
  };

  // Scroll right function
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth = 405.33 + 30; // card width + gap
      scrollContainerRef.current.scrollBy({
        left: cardWidth,
        behavior: 'smooth'
      });
    }
  };

  // Check scroll position on mount and when services data changes
  useEffect(() => {
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      checkScrollPosition();
    }, 200); // Increased delay to ensure everything is rendered
    
    // Add event listener for scroll events
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      // Also listen for resize events to recalculate on window resize
      window.addEventListener('resize', checkScrollPosition);
      
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
        clearTimeout(timer);
      };
    }
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="services-section">
      <div className="container">
        <div className="services-header">
          <div className="services-tag">
            Our Services
          </div>
          <h2 className="services-title">
            Everything Fixed, <span className="text-primary">All in One Place</span>
          </h2>
          <p className="services-description">
            From tech repairs to personal coaching, fixINN offers reliable, local services designed to make your life easier — all under one roof.
          </p>
        </div>

        <div className="services-container-wrapper">
          {/* Left scroll button */}
          {canScrollLeft && (
            <button 
              className="scroll-button scroll-button-left" 
              onClick={scrollLeft}
              aria-label="Scroll left"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          {/* Right scroll button */}
          {canScrollRight && (
            <button 
              className="scroll-button scroll-button-right" 
              onClick={scrollRight}
              aria-label="Scroll right"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          <div className="services-grid" ref={scrollContainerRef}>
            {Object.keys(servicesData).map((key, data) => {
              let service = servicesData[key];
              return (<div key={key} className="service-card">
                <div className="service-image">
                  <img src={service['image']} alt={service.title} />
                </div>
                <div className="service-content" style={{ padding: '24px' }}>
                  <h3 className="service-title">{service['title']}</h3>
                  <p className="service-description">{service['short_description']}</p>
                  <div className="service-divider"></div>
                  <Link to={service['link']} className="service-link">
                    Learn More
                  </Link>
                </div>
              </div>)
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
